/**
 * WhatsApi → API Keys no escopo organização (#25757, spec 2026-11-02 §5.2/§5.3): lista as
 * chaves da organização, abre o KB de cada uma e cria chave nova no gateway da organização,
 * pela MESMA rota e hook do detalhe do gateway (`useCriarChave`). Rotacionar, desativar e
 * excluir continuam no detalhe do gateway; cada linha leva até lá.
 *
 * O `pode` de criar é o `manage_keys` de GET /v1/whatsapp-gateways/organizacoes — o mesmo
 * gate da rota —, não o `pode` do recurso whatsapi (que vale para o KB).
 */
import { useState } from 'react'
import {
  ActionIcon, Alert, Anchor, Badge, Button, Center, Code, Group, Modal, Select, Skeleton, Stack,
  Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import { IconAlertCircle, IconBook, IconInfoCircle, IconKey, IconPlus } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { raizDaOrg, whatsapiOrgApi, type ChaveDaOrg, type OrgWhatsApi } from '@/api/whatsapiOrg'
import { useCriarChave, useGateways, useOrganizacoesWagw } from '@/hooks/useWhatsappGateways'
import { ChaveRevelada } from '@/components/admin/whatsappGateways/ChaveRevelada'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'
import { KbDrawer } from './KbDrawer'

const PREPARANDO = new Set(['provisionando', 'atualizando'])

const linkDoGateway = (orgId: number, gwId?: string) =>
  `/admin/whatsapp-gateways?org=${orgId}${gwId ? `&gw=${encodeURIComponent(gwId)}` : ''}`

function ultimoUso(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

interface ChavesDaOrganizacaoProps {
  orgId: number
  pode: OrgWhatsApi['pode']
}

export function ChavesDaOrganizacao({ orgId, pode }: ChavesDaOrganizacaoProps) {
  const qc = useQueryClient()
  const [kbChave, setKbChave] = useState<ChaveDaOrg | null>(null)
  const [novaAberta, setNovaAberta] = useState(false)
  const [nome, setNome] = useState('')
  const [gwEscolhido, setGwEscolhido] = useState<string | null>(null)
  const { data: chaves = [], isLoading, error } = useQuery({
    queryKey: ['whatsapi-org-api-keys', orgId],
    queryFn: () => whatsapiOrgApi.apiKeys(orgId),
    retry: false,
  })
  const orgsWagw = useOrganizacoesWagw()
  const podeWagw = orgsWagw.data?.find((o) => o.id === orgId)?.pode
  // GET .../whatsapp-gateways exige whatsapp_gateway:view; quem só lê o whatsapi não pergunta.
  const veGateways = Boolean(podeWagw?.view)
  const gateways = useGateways(veGateways ? orgId : null)
  const todos = gateways.data?.items ?? []
  const provisionados = todos.filter((g) => g.status === 'provisionado')
  const preparando = todos.find((g) => PREPARANDO.has(g.status))
  const gereChaves = veGateways && Boolean(podeWagw?.manage_keys)
  const podeCriar = gereChaves && provisionados.length > 0
  // Com um gateway só, ele é o destino; com mais de um, o modal pede (sem pré-seleção).
  const gwId = provisionados.length === 1 ? provisionados[0].id : gwEscolhido
  const criar = useCriarChave(orgId, gwId ?? '')
  const rotuloCriar = provisionados.length === 1 ? `Criar chave no gateway ${provisionados[0].name}` : 'Nova chave'

  function fecharNova() {
    setNovaAberta(false)
    setNome('')
    setGwEscolhido(null)
  }

  function enviarNova() {
    const n = nome.trim()
    if (!n || !gwId) return
    criar.mutate(n, {
      onSuccess: () => {
        fecharNova()
        // O hook relê as chaves do gateway; as listas desta tela ele não conhece.
        void qc.invalidateQueries({ queryKey: ['whatsapi-org-api-keys', orgId] })
        void qc.invalidateQueries({ queryKey: ['whatsapi-org-chaves', orgId] })
      },
    })
  }

  const botaoCriar = (variante: 'filled' | 'light') => (
    <Button variant={variante} size="xs" leftSection={<IconPlus size={14} />} onClick={() => setNovaAberta(true)}>
      {variante === 'filled' ? 'Nova chave' : rotuloCriar}
    </Button>
  )

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" style={{ flex: 1 }}>
          Rotacionar, desativar e excluir chave ficam no detalhe do gateway, em{' '}
          <Anchor href={linkDoGateway(orgId)}>Administração → Gateways WhatsApp</Anchor>.
          Aqui se cria chave e se mantém a Base de Conhecimento de cada uma.
        </Alert>
        {podeCriar && chaves.length > 0 && botaoCriar('filled')}
      </Group>

      {gateways.isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar os gateways da organização: {mensagemDeErro(gateways.error)}
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as chaves da organização. Tente novamente em instantes.
        </Alert>
      )}

      {error ? null : isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={32} radius="sm" />)}
        </Stack>
      ) : chaves.length === 0 ? (
        <Center py="xl">
          <Stack gap="xs" align="center">
            <IconKey size={40} color="var(--mantine-color-gray-5)" />
            {/* Spec 2026-11-02 §5.3, de baixo para cima: basta um gateway provisionado. */}
            {!veGateways || gateways.isLoading || gateways.isError ? (
              <Text c="dimmed" size="sm">Nenhuma chave de API nesta organização.</Text>
            ) : provisionados.length > 0 ? (
              <>
                <Text c="dimmed" size="sm">Nenhuma chave de API nesta organização.</Text>
                {gereChaves && botaoCriar('light')}
              </>
            ) : preparando ? (
              <Text c="dimmed" size="sm">O gateway {preparando.name} ainda está sendo preparado.</Text>
            ) : (
              <>
                <Text c="dimmed" size="sm">Esta organização ainda não tem gateway WhatsApp.</Text>
                {podeWagw?.manage ? (
                  <Anchor size="sm" href={linkDoGateway(orgId)}>Criar gateway</Anchor>
                ) : (
                  <Text c="dimmed" size="sm">Peça a um administrador da organização.</Text>
                )}
              </>
            )}
          </Stack>
        </Center>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Prefixo</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Gateway</Table.Th>
              <Table.Th>Último uso</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {chaves.map((k) => (
              <Table.Tr key={k.id}>
                <Table.Td><Text size="sm">{k.name}</Text></Table.Td>
                <Table.Td><Code>{k.key_prefix}...</Code></Table.Td>
                <Table.Td>
                  <Badge color={k.is_active ? 'teal' : 'gray'} variant="light">
                    {k.is_active ? 'Ativa' : 'Inativa'}
                  </Badge>
                </Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{k.gateway_id ?? k.gateway_legado ?? '—'}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{ultimoUso(k.last_used_at)}</Text></Table.Td>
                <Table.Td>
                  <Tooltip label="Base de Conhecimento">
                    <ActionIcon
                      aria-label={`Base de Conhecimento de ${k.name}`}
                      variant="subtle"
                      color="grape"
                      onClick={() => setKbChave(k)}
                    >
                      <IconBook size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {veGateways && k.gateway_id && (
                    <Anchor size="xs" ml="xs" href={linkDoGateway(orgId, k.gateway_id)}>
                      Gerenciar no gateway
                    </Anchor>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={novaAberta} onClose={fecharNova} title="Nova chave de API">
        <Stack>
          <Text size="sm">
            A chave nasce apontada para o gateway e envia pelo número dele. Ela aparece uma única vez, logo depois de criada.
          </Text>
          <TextInput
            label="Nome"
            description="De 1 a 64 caracteres: letras, números, espaço, ponto, hífen ou sublinhado. Único no WhatsApi."
            value={nome}
            maxLength={64}
            onChange={(e) => setNome(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') enviarNova() }}
            data-autofocus
          />
          {provisionados.length > 1 && (
            <Select
              label="Gateway"
              placeholder="Escolha o gateway"
              data={provisionados.map((g) => ({ value: g.id, label: g.name }))}
              value={gwEscolhido}
              onChange={setGwEscolhido}
              allowDeselect={false}
            />
          )}
          <Group>
            <Button variant="subtle" onClick={fecharNova}>Cancelar</Button>
            <Button disabled={!nome.trim() || !gwId} loading={criar.isPending} onClick={enviarNova}>Criar chave</Button>
          </Group>
        </Stack>
      </Modal>

      <ChaveRevelada segredo={criar.data?.key ?? null} nome={criar.data?.item.name ?? ''} onClose={() => criar.reset()} />

      <KbDrawer
        raiz={raizDaOrg(orgId)}
        apiKey={kbChave}
        pode={{ criar: pode.create, atualizar: pode.update, apagar: pode.delete }}
        opened={!!kbChave}
        onClose={() => setKbChave(null)}
      />
    </Stack>
  )
}
